import { I18n } from "@assets/strings";
import React from "react";
import styled from "styled-components";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { GroupMember } from "@domain/group/groupMember";
import { Profile } from "@domain/user/profile";
import { gap } from "@ui/styles/utils";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Secondary } from "@ui/components/containers/textBlocks";
import { row } from "@ui/components/layout";
import { FollowButton } from "@ui/components/profile/followButton";
import { UserView } from "@ui/components/profile/userView";

interface MembersListProps extends React.HTMLAttributes<HTMLDivElement> {
	members: Profile[];
	onShouldSeeMore: () => void;
	totalElements?: number;
	title: string;
	emptyText: string;
}
export const MembersList: React.FC<MembersListProps> = ({
	members,
	title,
	emptyText,
	onShouldSeeMore,
	totalElements,
	...htmlProps
}) => {
	const currentProfile = useCurrentProfile();

	if (totalElements === 0) {
		return (
			<RoundedBlock>
				<Empty>{emptyText}</Empty>
			</RoundedBlock>
		);
	}

	return (
		<Container {...htmlProps}>
			<Title>
				<PrimaryMedium>{title}</PrimaryMedium>
				<div onClick={() => onShouldSeeMore()}>{I18n.t("more_button")}</div>
			</Title>
			<RoundedBlock>
				{members.slice(0, 4).map((member) => (
					<StyledUserView
						unconfirmed={((member as GroupMember).status ?? "VALIDATED") !== "VALIDATED"}
						key={member.id}
						pictureSize={40}
						user={member}
						showDescription
						renderBottomAccessory={
							member.id !== currentProfile?.id ? () => <StyledFollowButton profile={member} /> : undefined
						}
					/>
				))}
			</RoundedBlock>
		</Container>
	);
};

const Title = styled.div`
	${row(10, "space-between", "center")}
	> :last-child {
		cursor: pointer;
		${({ theme }) => theme.textStyles.text};
		color: ${({ theme }) => theme.colors.secondary};
	}
`;
const Container = styled.div`
	${gap(15)};
`;
const Empty = styled(Secondary)`
	padding: 20px;
`;
const StyledUserView = styled(UserView)<{ unconfirmed: boolean }>`
	.profile-picture,
	.primaryName {
		${({ unconfirmed }) => unconfirmed && "opacity: 0.2"};
	}
	padding: 20px 16px;
	&:not(:last-child) {
		border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
	}
`;
const StyledFollowButton = styled(FollowButton)`
	margin-top: 5px;
`;
